<template>
  <div class="date-selector-wrapper">
    <label v-if="label" :for="dateSelectorId" class="date-selector__label">
      {{ label }}
      <span v-if="required" class="date-selector__required">*</span>
    </label>
    <div
      ref="triggerRef"
      :id="dateSelectorId"
      class="date-selector__trigger"
      :class="{
        'is-placeholder': !displayValue,
        'date-selector__trigger--open': isOpen,
        'date-selector__trigger--error': !!error,
        'date-selector__trigger--disabled': disabled,
      }"
      @click="toggleDropdown"
      @blur="handleBlur"
      :tabindex="disabled ? -1 : 0"
    >
      <span class="date-selector__trigger-text">
        {{ displayValue || "Select Date" }}
      </span>
      <Icon icon="mdi:calendar" class="date-selector__trigger-icon" />
    </div>

    <div v-if="error" class="date-selector__helper">
      <span class="date-selector__error">{{ error }}</span>
    </div>

    <Teleport to="body">
      <div v-if="isOpen" class="date-selector" :style="dropdownStyle">
        <div class="date-selector__header">
          <span class="date-selector__header-label">DATE</span>
        </div>

        <div class="date-selector__calendar">
          <div class="date-selector__calendar-header">
            <button class="date-selector__nav-button" @click.stop="previousMonth" type="button">
              <Icon icon="mdi:chevron-left" />
            </button>
            <span class="date-selector__month-year">{{ monthYearLabel }}</span>
            <button class="date-selector__nav-button" @click.stop="nextMonth" type="button">
              <Icon icon="mdi:chevron-right" />
            </button>
          </div>

          <div class="date-selector__weekdays">
            <div v-for="day in weekdays" :key="day" class="date-selector__weekday">
              {{ day }}
            </div>
          </div>

          <div class="date-selector__days">
            <div
              v-for="(day, index) in calendarDays"
              :key="index"
              class="date-selector__day"
              :class="{
                'date-selector__day--other-month': day.isOtherMonth,
                'date-selector__day--today': day.isToday,
                'date-selector__day--selected': day.isSelected,
                'date-selector__day--disabled': day.isDisabled,
              }"
              @click.stop="selectDate(day)"
            >
              {{ day.date }}
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { Icon } from "@/components/ui";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

interface CalendarDay {
  date: number;
  fullDate: string;
  isOtherMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isDisabled: boolean;
}

interface Props {
  modelValue?: string;
  minDate?: string;
  maxDate?: string;
  disablePast?: boolean;
  label?: string;
  required?: boolean;
  id?: string;
  error?: string;
  dateOnly?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  minDate: "",
  maxDate: "",
  disablePast: true,
  required: false,
  error: "",
  dateOnly: false,
  disabled: false,
});

const emit = defineEmits<{
  "update:modelValue": [value: string];
  blur: [event: FocusEvent];
}>();

const isOpen = ref(false);
const dropdownStyle = ref<Record<string, string>>({});
const triggerRef = ref<HTMLElement | null>(null);
const currentDate = ref(new Date());
let clickOutsideHandler: ((event: MouseEvent) => void) | null = null;

const dateSelectorId = computed(
  () => props.id || `date-selector-${Math.random().toString(36).substr(2, 9)}`
);

const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const monthYearLabel = computed(() => {
  const options: Intl.DateTimeFormatOptions = { month: "long", year: "numeric" };
  return currentDate.value.toLocaleDateString("en-US", options);
});

const parseDateString = (dateStr: string): Date => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  // Otherwise, use standard Date parsing
  return new Date(dateStr);
};

const displayValue = computed(() => {
  if (!props.modelValue) return "";
  const date = parseDateString(props.modelValue);
  if (isNaN(date.getTime())) return "";
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  };
  return date.toLocaleDateString("en-US", options);
});

const calendarDays = computed((): CalendarDay[] => {
  const year = currentDate.value.getFullYear();
  const month = currentDate.value.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();

  const days: CalendarDay[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const selectedDate = props.modelValue ? parseDateString(props.modelValue) : null;
  if (selectedDate) {
    selectedDate.setHours(0, 0, 0, 0);
  }

  const minDateObj = props.minDate ? parseDateString(props.minDate) : null;
  if (minDateObj) {
    minDateObj.setHours(0, 0, 0, 0);
  }

  const maxDateObj = props.maxDate ? parseDateString(props.maxDate) : null;
  if (maxDateObj) {
    maxDateObj.setHours(0, 0, 0, 0);
  }

  for (let i = 0; i < startingDayOfWeek; i++) {
    const prevMonthDate = new Date(year, month, 1 - startingDayOfWeek + i);
    days.push(
      createDayObject(
        prevMonthDate,
        true,
        today,
        selectedDate,
        minDateObj,
        maxDateObj,
        props.disablePast
      )
    );
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    days.push(
      createDayObject(date, false, today, selectedDate, minDateObj, maxDateObj, props.disablePast)
    );
  }

  const remainingDays = 42 - days.length;
  for (let day = 1; day <= remainingDays; day++) {
    const nextMonthDate = new Date(year, month + 1, day);
    days.push(
      createDayObject(
        nextMonthDate,
        true,
        today,
        selectedDate,
        minDateObj,
        maxDateObj,
        props.disablePast
      )
    );
  }

  return days;
});

const createDayObject = (
  date: Date,
  isOtherMonth: boolean,
  today: Date,
  selectedDate: Date | null,
  minDate: Date | null,
  maxDate: Date | null,
  disablePast: boolean
): CalendarDay => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const dateStr = `${year}-${month}-${day}`;

  const dateCopy = new Date(date);
  dateCopy.setHours(0, 0, 0, 0);

  const isToday = dateCopy.getTime() === today.getTime();
  const isSelected = selectedDate ? dateCopy.getTime() === selectedDate.getTime() : false;
  const isDisabled =
    (minDate && dateCopy < minDate) ||
    (maxDate && dateCopy > maxDate) ||
    (disablePast && dateCopy < today);

  return {
    date: date.getDate(),
    fullDate: dateStr,
    isOtherMonth,
    isToday,
    isSelected,
    isDisabled,
  };
};

const selectDate = (day: CalendarDay) => {
  if (props.disabled || day.isDisabled || day.isOtherMonth) return;
  const [year, month, dayNum] = day.fullDate.split("-").map(Number);
  if (props.dateOnly) {
    const pad = (n: number) => String(n).padStart(2, "0");
    emit("update:modelValue", `${year}-${pad(month)}-${pad(dayNum)}`);
  } else {
    emit("update:modelValue", new Date(year, month - 1, dayNum, 0, 0, 0, 0).toISOString());
  }
  setTimeout(() => {
    isOpen.value = false;
  }, 100);
};

const previousMonth = () => {
  currentDate.value = new Date(
    currentDate.value.getFullYear(),
    currentDate.value.getMonth() - 1,
    1
  );
};

const nextMonth = () => {
  currentDate.value = new Date(
    currentDate.value.getFullYear(),
    currentDate.value.getMonth() + 1,
    1
  );
};

const updateDropdownPosition = () => {
  if (!triggerRef.value) return;

  const rect = triggerRef.value.getBoundingClientRect();
  const spacing = 8;
  const dropdownHeight = 280;

  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const shouldShowAbove = spaceBelow < dropdownHeight + spacing && spaceAbove > spaceBelow;

  dropdownStyle.value = {
    position: "fixed",
    top: shouldShowAbove
      ? `${rect.top - dropdownHeight - spacing}px`
      : `${rect.bottom + spacing}px`,
    left: `${rect.left}px`,
    width: "290px",
    zIndex: "10100",
  };
};

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) isOpen.value = false;
  }
);

const toggleDropdown = () => {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    if (props.modelValue) {
      const date = parseDateString(props.modelValue);
      if (!isNaN(date.getTime())) {
        currentDate.value = new Date(date.getFullYear(), date.getMonth(), 1);
      }
    } else {
      currentDate.value = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    }
    setTimeout(() => {
      updateDropdownPosition();
    }, 0);
  }
};

const handleBlur = (event: FocusEvent) => {
  const relatedTarget = event.relatedTarget as HTMLElement;
  if (
    relatedTarget &&
    !relatedTarget.closest(".date-selector-wrapper") &&
    !relatedTarget.closest(".date-selector")
  ) {
    setTimeout(() => {
      if (isOpen.value) {
        isOpen.value = false;
      }
      emit("blur", event);
    }, 200);
  } else if (!relatedTarget) {
    setTimeout(() => {
      emit("blur", event);
    }, 100);
  }
};

onMounted(() => {
  clickOutsideHandler = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target && !target.closest(".date-selector-wrapper") && !target.closest(".date-selector")) {
      isOpen.value = false;
    }
  };
  document.addEventListener("click", clickOutsideHandler);
  window.addEventListener("scroll", updateDropdownPosition, true);
  window.addEventListener("resize", updateDropdownPosition);
});

onUnmounted(() => {
  if (clickOutsideHandler) {
    document.removeEventListener("click", clickOutsideHandler);
  }
  window.removeEventListener("scroll", updateDropdownPosition, true);
  window.removeEventListener("resize", updateDropdownPosition);
});
</script>

<style scoped>
.date-selector-wrapper {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.date-selector__label {
  display: block;
  font-size: 12px;
  font-weight: 400;
  color: #344054;
  margin-bottom: 6px;
}

.date-selector__required {
  color: #dc3545;
  font-size: 1.1rem;
  margin-left: 2px;
}

.date-selector__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 12px;
  background-color: #ffffff;
  border: 1px solid #ced4da;
  border-radius: 7px;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 400;
  min-height: 42px;
  transition: border-color 0.2s ease;
}

.date-selector__trigger:hover {
  border-color: var(--color-border-hover);
}

.date-selector__trigger:focus,
.date-selector__trigger--open {
  outline: none !important;
  border-color: var(--color-primary) !important;
  box-shadow: var(--ring) !important;
}

.date-selector__trigger--disabled {
  cursor: not-allowed;
  background-color: var(--color-bg-muted, #f3f4f6);
  color: var(--color-text-placeholder, #9ca3af);
  opacity: 0.7;
}

.date-selector__trigger--disabled:hover {
  border-color: #ced4da;
}

.date-selector__trigger-text {
  font-size: 0.82rem;
  font-weight: 400;
  flex: 1;
  text-align: left;
  color: #282a30;
}

.date-selector__trigger.is-placeholder .date-selector__trigger-text {
  color: #9ca3af;
}

.date-selector__trigger-icon {
  font-size: 1.1rem;
  color: #6b7280;
  transition: transform 0.2s ease;
}

.date-selector {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e4e4e7;
  box-shadow:
    0px 4px 6px -1px rgba(0, 0, 0, 0.1),
    0px 2px 4px -1px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 280px;
}

.date-selector__header {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 4px 8px;
  border-bottom: 1px solid #e4e4e7;
}

.date-selector__header-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #282a30;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.date-selector__calendar {
  padding: 4px;
}

.date-selector__calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.date-selector__nav-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid #e4e4e7;
  border-radius: 6px;
  background: #ffffff;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s ease;
  padding: 0;
  font-size: 1rem;
}

.date-selector__nav-button:hover {
  background-color: #f9fafb;
  border-color: #3e3efe;
  color: #3e3efe;
}

.date-selector__nav-button:active {
  background-color: #f3f4f6;
}

.date-selector__month-year {
  font-size: 0.8rem;
  font-weight: 600;
  color: #282a30;
}

.date-selector__weekdays {
  display: grid;
  grid-template-columns: repeat(7, auto);
  gap: 0px;
  margin-bottom: 2px;
  justify-content: center;
}

.date-selector__weekday {
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
  text-align: center;
  padding: 2px 0px;
  text-transform: uppercase;
  width: 36px;
}

.date-selector__days {
  display: grid;
  grid-template-columns: repeat(7, auto);
  gap: 4px;
  justify-content: center;
}

.date-selector__day {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 500;
  color: #282a30;
  cursor: pointer;
  border-radius: 2px;
  transition: all 0.15s ease;
  min-height: 20px;
  height: 36px;
  width: 36px;
}

.date-selector__day:hover:not(.date-selector__day--disabled):not(.date-selector__day--other-month) {
  background-color: #f3f4f6;
}

.date-selector__day--other-month {
  color: #d1d5db;
  cursor: default;
}

.date-selector__day--today {
  background-color: #eff6ff;
  color: #3e3efe;
  font-weight: 600;
}

.date-selector__day--selected {
  background-color: #3e3efe;
  color: #ffffff;
  font-weight: 600;
}

.date-selector__day--selected:hover {
  background-color: #3535e5;
}

.date-selector__day--disabled {
  color: #d1d5db;
  cursor: not-allowed;
  opacity: 0.5;
}

.date-selector__day--disabled:hover {
  background-color: transparent;
}

.date-selector__trigger--error {
  border-color: var(--color-danger) !important;
}

.date-selector__helper {
  margin-top: 4px;
  font-size: 0.7rem;
}

.date-selector__error {
  color: var(--color-danger);
}
</style>
